"use client";

import * as React from "react";
import styles from "./index.module.css";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { HubCardReorderTestResult } from "@/monarch/rules/hubCardReorder/types";
import { ReactNode } from "react";
import MonarchResolver from "@/monarch/MonarchResolver";

// This component is meant to be used for content blocks in WordPress that need to use Monarch.
// This prevents content blocks sitewide from loading monarch unecessarily.
export default function MonarchEnabled({ id, children }: any) {
  const handleHashChange = () => {
    const hash = window.location.hash.split("#")[1] || "";
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        // Apply 150px offset only for degree-faqs
        if (hash === "degree-faqs") {
          const elementPosition =
            element.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - 150;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        } else {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  };

  React.useEffect(() => {
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const reorderChildren = (
    children: ReactNode,
    data: HubCardReorderTestResult["data"],
  ) => {
    if (!data?.masters) return children;
    // Convert children to array for manipulation
    const childrenArray = React.Children.toArray(children);

    // Find all ProductCardListByUUIDClient components
    const productLists = childrenArray.filter((child: any) => {
      return child?.props?.shortcodeName === "product-card-list-by-uuid";
    });

    // If no product lists found, return original children
    if (productLists.length === 0) return children;
    // Create a map of product lists by their ctaUrl or copy
    const productListMap = new Map();
    productLists.forEach((list: any) => {
      const key = list.props.ctaUrl || list.props.copy;
      if (key) {
        productListMap.set(key, list);
      }
    });

    // Create reordered array of product lists
    const reorderedLists = data.masters
      .map((section) => {
        const key = section.ctaUrl || section.copy;
        return productListMap.get(key);
      })
      .filter(Boolean);

    // Add any remaining product lists that weren't in the reorder data
    const remainingLists = productLists.filter((list: any) => {
      const key = list.props.ctaUrl || list.props.copy;
      return !data.masters.some(
        (section) => section.ctaUrl === key || section.copy === key,
      );
    });

    // Combine reordered and remaining lists into a single queue
    const productListQueue = [...reorderedLists, ...remainingLists];

    // Create final array maintaining original positions
    const finalChildren = childrenArray.map((child: any) => {
      if (child?.props?.shortcodeName === "product-card-list-by-uuid") {
        // If it's a product list, take the next one from our queue
        return productListQueue.shift();
      }
      // If it's not a product list, keep it in its original position
      return child;
    });

    return finalChildren;
  };

  return (
    <MonarchResolver<HubCardReorderTestResult>
      experimentKey="useHubCardReorder"
      variants={{
        control: (
          <div className={styles.content} id={id}>
            {children}
          </div>
        ),
        reordered: (
          <MonarchDataConsumer<HubCardReorderTestResult>
            experimentKey="useHubCardReorder"
            render={({ data }) => {
              return (
                <div className={styles.content} id={id}>
                  {reorderChildren(children, data)}
                </div>
              );
            }}
          />
        ),
      }}
    />
  );
}
